import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useBranding } from "@/hooks/useBranding";
import BrandLogo from "@/components/BrandLogo";
import { ReactNode, useCallback } from "react";

const ROUTE_TITLES: Record<string, string> = {
  "/app/dashboard": "Home",
  "/app/wallets": "Wallets",
  "/app/exchange": "Exchange",
  "/app/transactions": "Transactions",
  "/app/spot": "Spot Markets",
   "/app/spot/orders": "Spot Orders",
   "/app/orders/spot": "Spot Orders",
   "/app/futures": "Futures",
   "/app/futures/orders": "Futures History",
   "/app/orders/futures": "Futures History",
  "/app/p2p": "P2P Market",
  "/app/p2p/orders": "P2P Orders",
  "/app/p2p/order": "Create Order",
  "/app/p2p/center": "P2P Center",
  "/app/p2p/merchant": "P2P Center",
  "/app/p2p/merchant-program": "Merchant Program",
  "/app/p2p/express": "P2P Express",
  "/app/p2p/block": "P2P Block",
  "/app/staking": "Staking",
  "/app/staking/positions": "My Staking",
  "/app/staking/positions/": "Dividends",
  "/app/staking/subscribe": "Stake",
  "/app/token-sale": "Token Sale",
  "/app/token-sale/portfolio": "My Tokens",
  "/app/token-sale/referrals": "Token Referrals",
  "/app/referrals": "Referrals",
  "/app/referrals/earnings": "Earnings",
  "/app/referrals/invites": "Invites",
  "/app/cards": "Cards",
  "/app/developer": "Developer",
  "/app/team": "Team",
  "/app/kyc": "Verification",
  "/app/security": "Security",
  "/app/settings": "Settings",
  "/app/support": "Support",
  "/app/notifications": "Notifications",
  "/app/orders": "Orders",
  "/app/crypto/deposit": "Deposit Crypto",
  "/app/crypto/withdraw": "Withdraw Crypto",
  "/app/crypto/transactions": "Crypto History",
  "/app/fiat/deposit": "Fiat Deposit",
  "/app/fiat/withdraw": "Fiat Withdrawal",
  "/app/trade/buy": "Buy Crypto",
  "/app/trade/sell": "Sell Crypto",
  "/app/help": "Help Center",
  "/app/verification": "Verification",
  "/app/payment-methods": "Payment Methods",
  "/app/more": "More",
};

const ROOT_ROUTES = ["/app/dashboard", "/app/spot", "/app/exchange", "/app/wallets", "/app/more"];

const FALLBACK_PARENTS: [string, string][] = [
  ["/app/wallets/", "/app/wallets"],
  ["/app/crypto/", "/app/wallets"],
  ["/app/fiat/", "/app/wallets"],
  ["/app/p2p/", "/app/p2p"],
  ["/app/spot/", "/app/spot"],
  ["/app/futures/", "/app/futures"],
  ["/app/staking/", "/app/staking"],
  ["/app/token-sale/", "/app/token-sale"],
  ["/app/referrals/", "/app/referrals"],
  ["/app/cards/", "/app/cards"],
  ["/app/kyc/", "/app/kyc"],
  ["/app/trade/", "/app/exchange"],
  ["/app/developer/", "/app/more"],
  ["/app/team/", "/app/more"],
];

function getFallbackParent(pathname: string): string {
  for (const [prefix, parent] of FALLBACK_PARENTS) {
    if (pathname.startsWith(prefix)) return parent;
  }
  return "/app/dashboard";
}

interface MobileHeaderProps {
  actions?: ReactNode;
}

export default function MobileHeader({ actions }: MobileHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { appName } = useBranding();

  const isRoot = ROOT_ROUTES.some((r) => location.pathname === r);
  const title = ROUTE_TITLES[location.pathname] ||
    Object.entries(ROUTE_TITLES).find(([prefix]) => location.pathname.startsWith(prefix + "/"))?.[1] ||
    "ACEX";

  const handleBack = useCallback(() => {
    const idx = (window.history.state as any)?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
    } else {
      navigate(getFallbackParent(location.pathname), { replace: true });
    }
  }, [navigate, location.pathname]);

  return (
    <header className="sticky z-40 glass-strong border-b border-border/50 h-14 flex items-center justify-between px-3 pt-[env(safe-area-inset-top)]" style={{ top: "var(--impersonation-banner-height, 0px)" }}>
      <div className="flex items-center min-w-0">
        {/* Fixed-width left slot to prevent layout shift */}
        <div className="w-10 shrink-0 flex items-center justify-center">
          {!isRoot && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
        </div>
        {isRoot ? (
          <div className="flex items-center gap-2">
            <BrandLogo className="h-7 max-w-[130px] object-contain" />
          </div>
        ) : (
          <h1 className="text-sm font-semibold text-foreground truncate">{title}</h1>
        )}
      </div>
      <div className="flex items-center gap-0.5">
        {actions}
        <ThemeToggle />
        <NotificationBell />
      </div>
    </header>
  );
}
